import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/auth';

const ParentFees = () => {
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchChildren();
  }, []);

  const fetchChildren = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const res = await api.get('/parent/children', {
        headers: { Authorization: `Bearer ${token}` }
      });

      const childrenList = Array.isArray(res.data) ? res.data : (res.data.children || []);

      // Pour chaque enfant, récupérer ses frais
      const childrenWithFees = await Promise.all(
        childrenList.map(async (child) => {
          try {
            const feesRes = await api.get(`/parent/children/${child.id}/fees`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            return {
              ...child,
              fees: feesRes.data.fees || [],
              total_due: parseFloat(feesRes.data.total_due) || 0,
              total_paid: parseFloat(feesRes.data.total_paid) || 0,
              total_pending: parseFloat(feesRes.data.total_pending) || 0,
              remaining: parseFloat(feesRes.data.remaining) || 0,
            };
          } catch (err) {
            console.error(`Erreur frais enfant ${child.id}:`, err);
            return {
              ...child,
              fees: [],
              total_due: 0,
              total_paid: 0,
              total_pending: 0,
              remaining: 0,
            };
          }
        })
      );

      setChildren(childrenWithFees);
    } catch (err) {
      console.error('Erreur:', err);
      setError(err.response?.data?.error || 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount) + ' FCFA';
  };

  const getProgressPercent = (totalDue, totalPaid) => {
    if (totalDue === 0) return 0;
    return Math.min(100, Math.round((totalPaid / totalDue) * 100));
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="h-48 bg-gray-200 rounded"></div>
          <div className="h-48 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4">
          <p className="text-red-700">❌ {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[#2C3E50]">💰 Frais scolaires</h1>
        <p className="text-gray-500">
          Situation financière de {children.length} enfant{children.length > 1 ? 's' : ''}
        </p>
      </div>

      {/* Info */}
      <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4">
        <p className="text-sm text-blue-700">
          ℹ️ <strong>Information :</strong> Pour effectuer un paiement, veuillez contacter
          l'administration de l'établissement. Le paiement en ligne sera bientôt disponible.
        </p>
      </div>

      {/* Aucun enfant */}
      {children.length === 0 && (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <p className="text-4xl mb-3">👧</p>
          <p className="text-lg font-medium text-gray-700 mb-2">Aucun enfant associé</p>
          <p className="text-sm text-gray-500">
            Contactez l'administration pour associer vos enfants à votre compte.
          </p>
        </div>
      )}

      {/* Cartes par enfant */}
      {children.map((child) => {
        const progress = getProgressPercent(child.total_due, child.total_paid);
        return (
          <div key={child.id} className="bg-white rounded-xl shadow-md overflow-hidden">
            {/* En-tête enfant */}
            <div className="p-5 border-b border-gray-200 bg-gradient-to-r from-orange-50 to-white">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 rounded-full bg-[#E67E22] text-white flex items-center justify-center text-xl font-bold">
                    {child.name ? child.name.charAt(0).toUpperCase() : '?'}
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-[#2C3E50]">
                      {child.name || 'Élève'}
                    </h2>
                    <p className="text-sm text-gray-500">
                      {child.class_name ? `Classe : ${child.class_name}` : 'Non assigné'}
                      {child.matricule ? ` • ${child.matricule}` : ''}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => navigate(`/parent/children/${child.id}/fees`)}
                  className="text-sm px-4 py-2 bg-[#E67E22] text-white rounded-lg hover:bg-[#D35400] transition"
                >
                  Voir le détail →
                </button>
              </div>
            </div>

            {/* Stats enfant */}
            <div className="p-5">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-gray-700">
                  <p className="text-xs text-gray-500 mb-1">📊 Total dû</p>
                  <p className="text-xl font-bold text-gray-800">
                    {formatAmount(child.total_due)}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-green-500">
                  <p className="text-xs text-gray-500 mb-1">✅ Déjà payé</p>
                  <p className="text-xl font-bold text-green-600">
                    {formatAmount(child.total_paid)}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-orange-500">
                  <p className="text-xs text-gray-500 mb-1">⏳ Reste à payer</p>
                  <p className="text-xl font-bold text-orange-600">
                    {formatAmount(child.remaining)}
                  </p>
                </div>
              </div>

              {/* Barre de progression */}
              {child.total_due > 0 && (
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <p className="text-xs font-medium text-gray-700">Progression</p>
                    <p className="text-xs font-bold text-[#E67E22]">{progress}%</p>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-gradient-to-r from-[#E67E22] to-[#D35400] h-3 rounded-full transition-all"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                </div>
              )}

              {/* Alerte si pending */}
              {child.total_pending > 0 && (
                <div className="mt-4 bg-yellow-50 border-l-4 border-yellow-500 rounded-lg p-3">
                  <p className="text-xs text-yellow-700">
                    ⏳ <strong>{formatAmount(child.total_pending)}</strong> en attente de validation.
                  </p>
                </div>
              )}

              {/* Message si pas de frais */}
              {child.fees.length === 0 && (
                <div className="mt-4 bg-gray-50 rounded-lg p-4 text-center text-sm text-gray-500">
                  📭 Aucun frais enregistré pour cet enfant
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ParentFees;